/**
 * One document, from the desk — approve, prepare, ship, cancel; or, when it
 * is a quotation, price it and send it, or decline it.
 *
 * Five decisions worth reading.
 *
 * **A quotation shows the quotation panel and nothing else.** Preparing and
 * shipping a document nobody has agreed to buy is meaningless, so those
 * controls are absent rather than disabled — and the panel that replaces them
 * asks for the one thing pricing adds, the number of days it stands. Declining
 * is beside it, because the desk that cannot price a request is the desk that
 * has to say so.
 *
 * **The two consequential actions are separated by permission and by place.**
 * Releasing a credit overrun sits at the top with the reason beside it;
 * picking and shipping sit on the lines. The server holds them under different
 * permissions — an ADV approves and cannot ship, the warehouse ships and
 * cannot approve — and the screen shows whichever the reader actually holds.
 *
 * **A shipment is composed, not implied.** The operator types what is going
 * out per line, and the default is what is outstanding — which is the common
 * case — while a partial departure is one edit away. Recording "the whole
 * order shipped" with one button would be the wrong default in a business
 * where a third of departures are partial.
 *
 * **Cancelling asks.** It releases stock and credits the balance back, neither
 * of which is undoable, and the reason goes on the record.
 *
 * **The provenance panel is here rather than hidden.** `external_ref` is where
 * an ERP's document number lands, and an ADV asked "is this in Sage yet?"
 * needs to answer without opening Sage.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { day, money, moment, quantity as formatQuantity } from "@hardauto/core-i18n";
import {
  Alert,
  Badge,
  Button,
  Card,
  DefinitionList,
  ErrorState,
  Input,
  LoadingState,
  Page,
  useToast,
} from "@hardauto/core-ui";

import { OrdersApi, type BackOfficeLine } from "../api";
import { NAMESPACE } from "../i18n";
import { ORDERS_PATHS } from "../paths";
import { STATUS_TONE } from "../status";
import "./orders.css";

export function OrderScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const toast = useToast();
  const action = useAction();
  const api = useMemo(() => new OrdersApi(client), [client]);
  const mayApprove = useCan("orders.order.approve");
  const mayFulfil = useCan("orders.fulfilment.write");

  const fetcher = useCallback(() => api.read(id), [api, id]);
  const found = useResource(fetcher);

  const [reason, setReason] = useState("");
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [toShip, setToShip] = useState<Record<string, string>>({});
  // Empty means "the installation's own default", which the policy holds —
  // so the desk types a number only when this quotation is an exception.
  const [validityDays, setValidityDays] = useState("");
  const [quoteComment, setQuoteComment] = useState("");

  if (found.loading && !found.data) {
    return (
      <Page title={t("detail.loading")}>
        <LoadingState label={t("detail.loading")} />
      </Page>
    );
  }
  if (!found.data) {
    return (
      <Page title={t("detail.notFound.title")}>
        <ErrorState
          title={t("detail.notFound.title")}
          description={t("detail.notFound.body")}
          {...(found.failed ? { onRetry: found.reload, retryLabel: t("error.retry") } : {})}
        />
      </Page>
    );
  }

  const { order, lines, shipments } = found.data;
  const held = order.status === "pending_approval";
  const isQuote = order.kind === "quote";
  const sendable = isQuote && order.status === "quote_requested";
  // Declining is possible right up to the customer's answer: a price the
  // distributor can no longer honour has to be withdrawable after it was sent.
  const declinable = isQuote && (sendable || order.status === "quote_sent");
  const shippable = isQuote
    ? []
    : lines.filter((line) => !line.is_cancelled && Number(line.outstanding) > 0);

  /** What the operator is about to ship, defaulted to everything outstanding. */
  function planned(line: BackOfficeLine): string {
    return toShip[line.id] ?? line.outstanding;
  }

  return (
    <Page
      title={t(isQuote ? "detail.quoteTitle" : "detail.title", { reference: order.reference })}
      eyebrow={
        order.placed_at
          ? t(isQuote ? "detail.requestedOn" : "detail.placedOn", {
              date: day(order.placed_at, locale),
            })
          : undefined
      }
      back={<Link to={ORDERS_PATHS.orders}>{t("detail.back")}</Link>}
      actions={
        <Badge tone={STATUS_TONE[order.status]}>
          {t(`status.${order.status}`, { defaultValue: order.status })}
        </Badge>
      }
    >
      <div className="hardauto-stack">
        {held ? (
          <Alert
            tone="warning"
            title={t(`holdReason.${found.data.hold_reason}`, {
              defaultValue: found.data.hold_reason,
            })}
          >
            {t("detail.held")}
          </Alert>
        ) : null}

        {held && mayApprove ? (
          <Card>
            <div className="hardauto-order__decision">
              <Button
                variant="primary"
                busy={action.isBusy("approve")}
                busyLabel={t("detail.approving")}
                onClick={() => {
                  void action.run("approve", async () => {
                    try {
                      await api.approve(order.id);
                      toast.show(t("detail.approved"), { tone: "success" });
                      found.reload();
                    } catch {
                      toast.show(t("error.title"), { tone: "danger" });
                    }
                  });
                }}
              >
                {t("detail.approve")}
              </Button>
            </div>
          </Card>
        ) : null}

        {isQuote && order.valid_until ? (
          <Alert
            tone="info"
            title={t("detail.quoteValidUntil", {
              date: day(order.valid_until, locale),
            })}
          >
            {t("detail.quoteSentNotice")}
          </Alert>
        ) : null}

        {/* Both ends of the link. "What did this quotation become" and "what
            was agreed before this order" are the same question asked from
            two screens. */}
        {order.converted_order_id ? (
          <p>
            <Link to={ORDERS_PATHS.order.replace("$id", order.converted_order_id)}>
              {t("detail.seeConvertedOrder")}
            </Link>
          </p>
        ) : null}
        {order.quote_id ? (
          <p>
            <Link to={ORDERS_PATHS.order.replace("$id", order.quote_id)}>
              {t("detail.seeSourceQuote")}
            </Link>
          </p>
        ) : null}

        {mayApprove && (sendable || declinable) ? (
          <Card title={t("detail.quotePanel")} description={t("detail.quotePanelHint")}>
            <div className="hardauto-stack">
              {sendable ? (
                <form
                  className="hardauto-inline-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void action.run("sendQuote", async () => {
                      try {
                        await api.sendQuote(order.id, {
                          // Zero means "use the installation's default", which
                          // is what an empty field means to the reader too.
                          validity_days: Number(validityDays) || 0,
                          comment: quoteComment,
                        });
                        toast.show(t("detail.quoteSent"), { tone: "success" });
                        setValidityDays("");
                        setQuoteComment("");
                        found.reload();
                      } catch {
                        toast.show(t("error.title"), { tone: "danger" });
                      }
                    });
                  }}
                >
                  <div className="hardauto-inline-form__field">
                    <Field label={t("detail.validityDays")} hint={t("detail.validityHint")}>
                      {(props) => (
                        <Input
                          {...props}
                          type="number"
                          min="1"
                          max="365"
                          inputMode="numeric"
                          value={validityDays}
                          onChange={(event) => setValidityDays(event.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="hardauto-inline-form__field">
                    <Field label={t("detail.quoteComment")}>
                      {(props) => (
                        <Input
                          {...props}
                          value={quoteComment}
                          onChange={(event) => setQuoteComment(event.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="hardauto-inline-form__actions">
                    <Button
                      type="submit"
                      variant="primary"
                      busy={action.isBusy("sendQuote")}
                      busyLabel={t("detail.sendingQuote")}
                    >
                      {t("detail.sendQuote")}
                    </Button>
                  </div>
                </form>
              ) : null}

              {declinable ? (
                <form
                  className="hardauto-inline-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void action.run("declineQuote", async () => {
                      try {
                        await api.declineQuote(order.id, reason);
                        toast.show(t("detail.quoteDeclined"), { tone: "success" });
                        setReason("");
                        found.reload();
                      } catch {
                        toast.show(t("error.title"), { tone: "danger" });
                      }
                    });
                  }}
                >
                  <div className="hardauto-inline-form__field">
                    <Field label={t("detail.declineReason")}>
                      {(props) => (
                        <Input
                          {...props}
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="hardauto-inline-form__actions">
                    <Button
                      type="submit"
                      variant="danger"
                      busy={action.isBusy("declineQuote")}
                      busyLabel={t("detail.decliningQuote")}
                    >
                      {t("detail.declineQuote")}
                    </Button>
                  </div>
                </form>
              ) : null}
            </div>
          </Card>
        ) : null}

        <div className="hardauto-orders__split">
          <Card title={t("detail.delivery")} compactTitle>
            <DefinitionList
              items={[
                { label: t("list.column.site"), value: order.delivery.site_code },
                {
                  label: t("detail.delivery"),
                  value:
                    [order.delivery.address, order.delivery.city].filter(Boolean).join(" · ") ||
                    "—",
                },
                {
                  label: t("detail.customerReference"),
                  value: order.delivery.customer_reference || "—",
                },
              ]}
            />
            {order.delivery.comment ? (
              <p className="hardauto-cell__muted">{order.delivery.comment}</p>
            ) : null}
          </Card>

          <Card title={t("detail.summary")} compactTitle>
            <dl className="hardauto-totals">
              <dt>{t("detail.subtotal")}</dt>
              <dd className="hardauto-num">{money(order.subtotal, locale)}</dd>
              <dt>{t("detail.tax", { rate: formatQuantity(order.tax_rate_pct, locale) })}</dt>
              <dd className="hardauto-num">{money(order.tax_amount, locale)}</dd>
              <dt>{t("detail.shipping")}</dt>
              <dd className="hardauto-num">
                {Number(order.shipping_amount) === 0
                  ? t("detail.shippingFree")
                  : money(order.shipping_amount, locale)}
              </dd>
              <dt className="hardauto-totals__total">{t("detail.total")}</dt>
              <dd className="hardauto-num hardauto-totals__total">{money(order.total, locale)}</dd>
            </dl>
            {order.payment_terms ? (
              <DefinitionList
                items={[{ label: t("detail.paymentTerms"), value: order.payment_terms }]}
              />
            ) : null}
          </Card>

          <Card title={t("detail.provenance")} compactTitle>
            {/* An ADV asked "is this in Sage yet?" answers from here rather
                than by opening Sage. */}
            <DefinitionList
              items={[
                { label: t("detail.origin"), value: found.data.origin },
                {
                  label: t("detail.externalRef"),
                  value: found.data.external_ref ?? t("detail.notSynced"),
                },
                { label: t("detail.syncState"), value: found.data.sync_state },
              ]}
            />
          </Card>
        </div>

        <Card title={t("detail.lines")} flush>
          <table className="hardauto-table">
            <caption className="hardauto-visually-hidden">{t("detail.lines")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("detail.column.article")}</th>
                <th scope="col">
                  {t(isQuote ? "detail.column.quantity" : "detail.column.ordered")}
                </th>
                {/* Absent rather than zeroed on a quotation: nothing is
                    picked or shipped against a price nobody has accepted. */}
                {isQuote ? null : (
                  <>
                    <th scope="col">{t("detail.column.prepared")}</th>
                    <th scope="col">{t("detail.column.shipped")}</th>
                    <th scope="col">{t("detail.column.outstanding")}</th>
                    <th scope="col">{t("detail.column.status")}</th>
                  </>
                )}
                <th scope="col">{t("detail.column.amount")}</th>
                {mayFulfil && !isQuote ? (
                  <th scope="col">
                    <span className="hardauto-visually-hidden">{t("detail.prepare")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id} data-cancelled={line.is_cancelled ? "true" : undefined}>
                  <th scope="row">
                    <div className="hardauto-cell__stack">
                      <span>{line.designation}</span>
                      <span className="hardauto-cell__muted">
                        {line.reference} · {line.site_code}
                      </span>
                    </div>
                  </th>
                  <td>
                    <span className="hardauto-num">{formatQuantity(line.quantity, locale)}</span>
                  </td>
                  {isQuote ? null : (
                    <>
                      <td>
                        <span className="hardauto-num">
                          {formatQuantity(line.quantity_prepared, locale)}
                        </span>
                      </td>
                      <td>
                        <span className="hardauto-num">
                          {formatQuantity(line.quantity_shipped, locale)}
                        </span>
                      </td>
                      <td>
                        <span className="hardauto-num">
                          {formatQuantity(line.outstanding, locale)}
                        </span>
                      </td>
                      <td className="hardauto-cell__muted">
                        {t(`lineStatus.${line.status}`, { defaultValue: line.status })}
                      </td>
                    </>
                  )}
                  <td>
                    <span className="hardauto-num">{money(line.amount, locale)}</span>
                  </td>
                  {mayFulfil && !isQuote ? (
                    <td>
                      {Number(line.outstanding) > 0 ? (
                        <Button
                          size="sm"
                          busy={action.isBusy(`prepare:${line.id}`)}
                          busyLabel={t("detail.preparingBusy")}
                          onClick={() => {
                            void action.run(`prepare:${line.id}`, async () => {
                              try {
                                await api.markPrepared(order.id, line.id, line.quantity);
                                toast.show(t("detail.preparedDone"), { tone: "success" });
                                found.reload();
                              } catch {
                                toast.show(t("error.title"), { tone: "danger" });
                              }
                            });
                          }}
                        >
                          {t("detail.prepare")}
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {mayFulfil && shippable.length > 0 ? (
          <Card title={t("detail.ship")}>
            <form
              className="hardauto-inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                const payload = shippable
                  .map((line) => ({ line_id: line.id, quantity: planned(line) }))
                  .filter((entry) => Number(entry.quantity) > 0);
                if (payload.length === 0) return;
                void action.run("ship", async () => {
                  try {
                    await api.ship(order.id, {
                      lines: payload,
                      carrier,
                      tracking_reference: tracking,
                    });
                    toast.show(t("detail.shippedDone"), { tone: "success" });
                    setToShip({});
                    setCarrier("");
                    setTracking("");
                    found.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                });
              }}
            >
              {shippable.map((line) => (
                <div className="hardauto-inline-form__field" key={line.id}>
                  <Field label={`${line.reference} — ${t("detail.shipQuantity")}`}>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min="0"
                        step="1"
                        max={line.outstanding}
                        value={planned(line)}
                        onChange={(event) =>
                          setToShip((current) => ({
                            ...current,
                            [line.id]: event.target.value,
                          }))
                        }
                      />
                    )}
                  </Field>
                </div>
              ))}
              <div className="hardauto-inline-form__field">
                <Field label={t("detail.carrier")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={carrier}
                      onChange={(event) => setCarrier(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__field">
                <Field label={t("detail.tracking")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={tracking}
                      onChange={(event) => setTracking(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__actions">
                <Button
                  type="submit"
                  variant="primary"
                  busy={action.isBusy("ship")}
                  busyLabel={t("detail.shippingBusy")}
                >
                  {t("detail.ship")}
                </Button>
              </div>
            </form>
          </Card>
        ) : null}

        {isQuote ? null : (
          <Card title={t("detail.shipments")}>
            {shipments.length === 0 ? (
              <p className="hardauto-cell__muted">{t("detail.shipmentsEmpty")}</p>
            ) : (
              <ul className="hardauto-media">
                {shipments.map((shipment) => (
                  <li key={shipment.id} className="hardauto-media__row">
                    <span className="hardauto-media__label">
                      {t("detail.shipment", { reference: shipment.reference })}
                      {shipment.shipped_at ? ` · ${moment(shipment.shipped_at, locale)}` : ""}
                    </span>
                    <span className="hardauto-cell__muted">
                      {[shipment.carrier, shipment.tracking_reference].filter(Boolean).join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {mayApprove && order.is_open && !isQuote ? (
          <Card>
            <form
              className="hardauto-inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                void action.run("cancel", async () => {
                  try {
                    await api.cancel(order.id, reason);
                    toast.show(t("detail.cancelled"), { tone: "success" });
                    setReason("");
                    found.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                });
              }}
            >
              <div className="hardauto-inline-form__field">
                <Field label={t("detail.cancelReason")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__actions">
                {/* Releases stock and credits the balance back, neither of
                    which is undoable — hence the reason field beside it. */}
                <Button
                  type="submit"
                  variant="danger"
                  busy={action.isBusy("cancel")}
                  busyLabel={t("detail.cancelling")}
                >
                  {t("detail.cancel")}
                </Button>
              </div>
            </form>
          </Card>
        ) : null}
      </div>
    </Page>
  );
}
